import React, { useState, useEffect } from 'react';
import { refreshAppCheckToken, getAppCheckStatus } from '../../services/firebaseService';
import { appCheckConfig } from '../../config/appCheck';

interface AppCheckStatus {
  initialized: boolean;
  hasToken: boolean;
  tokenPreview?: string | null;
  error?: string;
}

export const AppCheckDebugPanel: React.FC = () => {
  const [status, setStatus] = useState<AppCheckStatus | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const checkStatus = async () => {
    const currentStatus = await getAppCheckStatus();
    setStatus(currentStatus);
  };

  const handleRefreshToken = async () => {
    setIsRefreshing(true);
    try {
      const success = await refreshAppCheckToken();
      if (success) {
        setLastRefresh(new Date());
        await checkStatus(); // Refresh status after token refresh
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    checkStatus();
    // Check status every 30 seconds
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Only show in development or when there are issues
  if (!appCheckConfig.isDevelopment && status?.hasToken) {
    return null;
  }

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 bg-red-500 text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg hover:bg-red-600 transition-colors z-50"
        title="App Check Debug Panel"
      >
        🔥 App Check
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 bg-white border border-gray-300 rounded-lg shadow-xl p-4 max-w-sm z-50">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-gray-800">🔥 App Check Status</h3>
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span>Initialized:</span>
          <span className={status?.initialized ? 'text-green-600' : 'text-red-600'}>
            {status?.initialized ? '✅' : '❌'}
          </span>
        </div>

        <div className="flex justify-between">
          <span>Has Token:</span>
          <span className={status?.hasToken ? 'text-green-600' : 'text-red-600'}>
            {status?.hasToken ? '✅' : '❌'}
          </span>
        </div>

        {status?.tokenPreview && (
          <div className="flex justify-between">
            <span>Token:</span>
            <span className="text-gray-600 font-mono text-xs">
              {status.tokenPreview}
            </span>
          </div>
        )}

        {status?.error && (
          <div className="text-red-600 text-xs bg-red-50 p-2 rounded">
            {status.error}
          </div>
        )}

        <div className="flex justify-between">
          <span>Environment:</span>
          <span className={appCheckConfig.isDevelopment ? 'text-blue-600' : 'text-green-600'}>
            {appCheckConfig.isDevelopment ? 'Dev' : 'Prod'}
          </span>
        </div>

        <div className="flex justify-between">
          <span>Site Key:</span>
          <span className="text-gray-600 font-mono text-xs">
            {appCheckConfig.recaptchaSiteKey.substring(0, 10)}...
          </span>
        </div>

        {lastRefresh && (
          <div className="text-xs text-gray-500">
            Last refresh: {lastRefresh.toLocaleTimeString()}
          </div>
        )}
      </div>

      <div className="mt-4 space-y-2">
        <button
          onClick={handleRefreshToken}
          disabled={isRefreshing}
          className="w-full bg-blue-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-blue-600 disabled:bg-gray-400 transition-colors"
        >
          {isRefreshing ? 'Refreshing...' : 'Refresh Token'}
        </button>

        <button
          onClick={checkStatus}
          className="w-full bg-gray-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-gray-600 transition-colors"
        >
          Check Status
        </button>

        {!status?.hasToken && (
          <div className="text-xs text-gray-600 bg-yellow-50 p-2 rounded">
            💡 If you're getting auth errors, try refreshing the token or check your ReCaptcha configuration.
          </div>
        )}
      </div>
    </div>
  );
}; 